import { UrlObject } from 'url';

import { AnchorHTMLAttributes, DetailedHTMLProps, forwardRef } from 'react';
import Link, { LinkProps } from 'next/link';
import { clsx } from 'clsx';

import { PROD_DOMAIN, PROJECT_SLUG, STAGING_DOMAIN } from '@monoimin/constants';

import { InvalidLinkMessage } from './InvalidLinkMessage';
import { LinkFullSizeOverlay } from './LinkFullSizeOverlay';

export type ReactAnchorProps = DetailedHTMLProps<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

type AnchorAttributes = Omit<ReactAnchorProps, 'href'>;

type NextLinkProps = Omit<
  LinkProps,
  'passHref' | 'onTouchStart' | 'onMouseEnter' | 'onClick' | 'locale'
>;

export interface SharedLinkProps
  extends AnchorAttributes,
    NextLinkProps,
    ComponentCommonProps {
  linkType?: 'internal' | 'external';
  underlineSize?: 0 | 1 | 2 | 4 | 8 | 'from-font' | 'auto';
  underline?: 'hover' | 'on' | 'off';
  'data-testid'?: string;
  'data-theme'?: string;
  fullSizeOverlay?: boolean | undefined;
}

const domainsByProject = {
  monoimin: 'https://immigrantinvest.com',
  passportivity: 'https://passportivity.com/real-estate',
  'passport-rating': '',
  'golden-visa': 'https://mygoldenvisa.io',
  minisites: '',
};
const domainToRemove = domainsByProject[PROJECT_SLUG];
const basePath = process.env['NEXT_PUBLIC_BASE_PATH'] || '';
export const SharedLink = forwardRef<HTMLAnchorElement, SharedLinkProps>(
  (
    {
      children,
      href,
      replace,
      scroll,
      prefetch,
      className,
      underlineSize = 1,
      underline = 'hover',
      linkType = 'internal',
      target,
      isRichText,
      fullSizeOverlay,
      ...props
    },
    ref
  ) => {
    const hrefWithoutDomain = href?.toString().replace(domainToRemove, '');

    if (!hrefWithoutDomain) {
      return <InvalidLinkMessage />;
    }
    const actualLinkType =
      hrefWithoutDomain.toString().startsWith('http') ||
      hrefWithoutDomain.toString().startsWith('tel:') ||
      hrefWithoutDomain.toString().startsWith('mailto:')
        ? 'external'
        : linkType;

    const allClassnames = clsx(
      {
        underline: underline === 'on',
        'hover:underline': underline === 'hover',
        'no-underline': underline === 'off',
      },
      `underline-offset-size-link decoration-${underlineSize}`,
      className,
      'shared-link',
      isRichText && 'shared-link-rich-text'
    );
    if (actualLinkType === 'internal') {
      const internalHref =
        typeof href === 'string' ? `${basePath}${href}` : href;

      return (
        <Link
          href={normalizeHref(internalHref)}
          target={target}
          replace={replace}
          scroll={scroll}
          prefetch={prefetch}
          className={allClassnames}
          aria-label={props['aria-label']}
          data-testid={props['data-testid']}
          id={props.id}
          ref={ref}
          data-theme={props['data-theme']}
          data-link-is-external={
            //Todo: удалить при релизе кнопок
            String(href).includes('immigrantinvest.com') ||
            String(href).includes('mygoldenvisa.io')
          }
        >
          {children}
          {fullSizeOverlay && <LinkFullSizeOverlay />}
        </Link>
      );
    }

    return (
      <a
        href={formatHref(normalizeHref(href))}
        target={
          String(href).includes('immigrantinvest.com') ||
          String(href).startsWith('#')
            ? target ?? '_self'
            : target ?? '_blank'
        }
        rel='noreferrer'
        {...props}
        ref={ref}
        data-theme={props['data-theme']}
        className={allClassnames}
        data-link-is-external='true'
      >
        {children}
        {fullSizeOverlay && <LinkFullSizeOverlay />}
      </a>
    );
  }
);

SharedLink.displayName = 'SharedLink';

const domainsForFormat = ['immigrantinvest.com', 'immigrantinvest.eu'];

const normalizeHref = (href: string | UrlObject) => {
  if (typeof href === 'string') {
    return href.trim();
  }

  return String(href);
};

const formatHref = (href: string) => {
  if (
    href.includes('mygoldenvisa.io') &&
    href.endsWith('/') &&
    !domainsForFormat.includes(PROD_DOMAIN as string) &&
    !domainsForFormat.includes(STAGING_DOMAIN as string)
  ) {
    return href.slice(0, -1);
  }

  if (
    domainsForFormat.some((d) => href.includes(d)) &&
    !href.endsWith('/') &&
    !endsWithFileFormat(href) &&
    !href.includes('#')
  )
    return href + '/';

  return href;
};
const fileFormats = ['.jpg', '.jpeg', '.png', '.pdf', '.svg', '.webp'];

function endsWithFileFormat(inputString: string) {
  return fileFormats.some((suffix) => {
    const escapedSuffix = suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedSuffix + '$', 'i');

    return regex.test(inputString);
  });
}
